import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { stallplatserFaq, stallplatserSections } from "@/data/nationella-guider";

const URL = "https://www.akhusbilfritid.se/stallplatser-husbil";

export const Route = createFileRoute("/stallplatser-husbil")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(stallplatserFaq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Ställplatser husbil 2026: pris per dygn, regler & vinter" },
      {
        name: "description",
        content:
          "Vad en ställplats kostar per dygn, var du faktiskt får stå över natten, vilka platser är öppna på vintern och hur många dygn du klarar utan el.",
      },
      {
        name: "keywords",
        content:
          "ställplatser husbil, ställplatser sverige, ställplats pris, vildcamping husbil, husbil övernattning regler, vinterställplats husbil",
      },
      { property: "og:type", content: "article" },
      { property: "og:title", content: "Ställplatser husbil 2026: pris per dygn, regler & vinter" },
      {
        property: "og:description",
        content:
          "Pris per dygn, reglerna för att stå över natten och hur länge vatten, tank och batteri räcker. Siffror från vår egen verkstad.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `${URL}#article`,
          headline: "Ställplatser för husbil i Sverige – priser, regler och säsong",
          url: URL,
          inLanguage: "sv-SE",
          about: "Ställplatser för husbil i Sverige",
          author: { "@type": "Organization", name: "AK Husbil & Fritid" },
          publisher: { "@type": "Organization", name: "AK Husbil & Fritid" },
        }),
      },
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Guider", item: "https://www.akhusbilfritid.se/guider" },
            { "@type": "ListItem", position: 3, name: "Ställplatser för husbil", item: URL },
          ],
        }),
      },
    ],
  }),
  component: StallplatserPage,
});

const facts = [
  { k: "150–350 kr", v: "normalt dygnspris på ställplats" },
  { k: "25–40 l", v: "vattenförbrukning per dygn, två personer" },
  { k: "1,4–1,8 kg", v: "gasol per dygn vid −20 °C utan el" },
  { k: "24 h", v: "max parkering på allmän plats utan skyltning" },
];

function StallplatserPage() {
  return (
    <>
      <PageHeader
        label="Nationell guide · Ställplatser"
        title={
          <>
            Ställplatser i Sverige — <span className="font-italic-display text-accent">utan</span> gissningar.
          </>
        }
        sub="Vad de kostar, vad reglerna faktiskt säger, vilka som har öppet på vintern och hur länge du klarar dig utan el. Siffrorna kommer från våra egna mätningar och från kunder som kör sträckan varje år."
      />

      <section className="container-edge pt-14 md:pt-20">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((f) => (
            <div key={f.k} className="rounded-3xl border border-border bg-card p-6">
              <p className="font-display text-3xl leading-none text-accent">{f.k}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.v}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="pt-14 md:pt-20">
        <HubEditorial eyebrow="Guiden" sections={stallplatserSections} faq={stallplatserFaq} />
      </div>

      <section className="container-edge pb-20 md:pb-24">
        <div className="rounded-3xl border border-border bg-warm p-7 md:p-10">
          <p className="eyebrow">Läs vidare</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <Link
              to="/solceller-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Solceller & litium — så står du fritt längre</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/tester"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Våra långtidstester med uppmätt förbrukning</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/fordon"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Fordon i lager — fukt- och gasoltestade</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
